/**
 * Poseidon dashboard — charts.
 *
 * Library choice: **recharts** (see README, "Charting choice"). Every §3.2 B
 * chart is a line, area, bar or histogram — recharts covers all of them
 * declaratively and keeps the whole dashboard in React components with no
 * imperative canvas lifecycle to manage. Candle rendering, which recharts does
 * not do well, is explicitly a placeholder here (see `ChartPlaceholder`) because
 * the bar feed does not exist yet; `lightweight-charts` is the intended
 * replacement for that one panel, and the README says so.
 *
 * Each chart takes the typed `ChartSeries` the daemon is contracted to send, so
 * nothing is recomputed in the browser and the placeholder-to-real swap is a
 * data change, not a rewrite.
 */
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { AnalyticsChartId, ChartSeries } from '../api/types';
import { formatCompact, formatNumber } from '../lib/format';
import { Panel } from './primitives';

const COLOR_TOKENS: Record<string, string> = {
  'series-1': 'var(--series-1)',
  'series-2': 'var(--series-2)',
  'series-3': 'var(--series-3)',
  'series-4': 'var(--series-4)',
};

const AXIS_STYLE = { fill: 'var(--text-faint)', fontSize: 11 } as const;

const TOOLTIP_STYLE = {
  background: 'var(--bg-raised)',
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  fontSize: 12,
} as const;

function colorFor(token: string): string {
  return COLOR_TOKENS[token] ?? 'var(--series-1)';
}

/** Histogram / categorical bar chart. */
export function BarSeriesChart({ series, height = 260 }: { series: ChartSeries; height?: number }): JSX.Element {
  const xKey = 'bucket';
  return (
    <div className="chart-frame" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={series.points} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="2 3" vertical={false} />
          <XAxis dataKey={xKey} tick={AXIS_STYLE} stroke="var(--border-strong)" interval={0} angle={series.points.length > 8 ? -30 : 0} textAnchor={series.points.length > 8 ? 'end' : 'middle'} height={series.points.length > 8 ? 58 : 30} />
          <YAxis tick={AXIS_STYLE} stroke="var(--border-strong)" tickFormatter={(value: number) => formatCompact(value)} />
          {/* recharts v3 passes `ValueType | undefined`; coerce explicitly rather
              than asserting, so a missing value renders as an em dash. */}
          <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={{ color: 'var(--text)' }} formatter={(value: unknown) => formatNumber(typeof value === 'number' ? value : null, 2)} />
          <ReferenceLine y={0} stroke="var(--border-strong)" />
          {series.series.map((entry, index) => (
            <Bar key={entry.key} dataKey={entry.key} name={entry.label} fill={colorFor(entry.color_token)} radius={[2, 2, 0, 0]}>
              {index === 0 &&
                series.points.map((point) => {
                  const value = point[entry.key];
                  const negative = typeof value === 'number' && value < 0;
                  return <Cell key={String(point[xKey])} fill={negative ? 'var(--danger)' : colorFor(entry.color_token)} />;
                })}
            </Bar>
          ))}
          {series.series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Line / area chart with an optional filled series (equity curve). */
export function LineSeriesChart({
  series,
  height = 260,
  area = false,
}: {
  series: ChartSeries;
  height?: number;
  area?: boolean;
}): JSX.Element {
  const xKey = series.points.some((point) => 'at' in point) ? 'at' : 'label';
  return (
    <div className="chart-frame" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={series.points} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="2 3" vertical={false} />
          <XAxis
            dataKey={xKey}
            tick={AXIS_STYLE}
            stroke="var(--border-strong)"
            tickFormatter={(value: string) => (xKey === 'at' ? new Date(value).toLocaleTimeString(undefined, { hour12: false }) : value)}
            minTickGap={28}
          />
          <YAxis tick={AXIS_STYLE} stroke="var(--border-strong)" tickFormatter={(value: number) => formatCompact(value)} domain={['auto', 'auto']} />
          <Tooltip
            contentStyle={TOOLTIP_STYLE}
            labelStyle={{ color: 'var(--text)' }}
            labelFormatter={(label: unknown) =>
              xKey === 'at' && typeof label === 'string' ? new Date(label).toLocaleString(undefined, { hour12: false }) : String(label ?? '')
            }
            formatter={(value: unknown) => formatNumber(typeof value === 'number' ? value : null, 2)}
          />
          <ReferenceLine y={0} stroke="var(--border-strong)" />
          {series.series.map((entry) =>
            area ? (
              <Area
                key={entry.key}
                type="monotone"
                dataKey={entry.key}
                name={entry.label}
                stroke={colorFor(entry.color_token)}
                fill={colorFor(entry.color_token)}
                fillOpacity={0.14}
                dot={false}
                strokeWidth={1.6}
              />
            ) : (
              <Line
                key={entry.key}
                type="monotone"
                dataKey={entry.key}
                name={entry.label}
                stroke={colorFor(entry.color_token)}
                dot={false}
                strokeWidth={1.6}
              />
            ),
          )}
          {series.series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Chart inside the standard panel chrome, with honest provenance footnotes. */
export function ChartPanel({
  series,
  kind,
  height,
  mock = false,
}: {
  series: ChartSeries;
  kind: 'bar' | 'line' | 'area' | 'histogram';
  height?: number;
  mock?: boolean;
}): JSX.Element {
  const isBar = kind === 'bar' || kind === 'histogram';
  return (
    <Panel
      title={series.title}
      subtitle={series.subtitle ?? undefined}
      mock={mock}
      footnote={
        <>
          {series.sample_size} observation{series.sample_size === 1 ? '' : 's'} · unit {series.unit} · as of{' '}
          {new Date(series.as_of).toLocaleTimeString(undefined, { hour12: false })}
          {series.notes.length > 0 && (
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {series.notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          )}
        </>
      }
    >
      {series.points.length === 0 ? (
        <div className="chart-placeholder">
          <strong>No observations in this window</strong>
          <span>The daemon returned an empty series. Nothing is being estimated or interpolated.</span>
        </div>
      ) : isBar ? (
        <BarSeriesChart series={series} height={height ?? 260} />
      ) : (
        <LineSeriesChart series={series} height={height ?? 260} area={kind === 'area'} />
      )}
    </Panel>
  );
}

/**
 * Explicit placeholder for a panel whose backing data does not exist yet.
 * §3.2 B and §3.2 C list charts this skeleton cannot honestly render without a
 * bar/tick feed, so they say so instead of drawing something plausible.
 */
export function ChartPlaceholder({
  title,
  requirement,
  plan,
}: {
  title: string;
  requirement: string;
  plan: string;
}): JSX.Element {
  return (
    <Panel title={title} subtitle="placeholder — no data source yet">
      <div className="chart-placeholder">
        <strong>{title}</strong>
        <span>{requirement}</span>
        <span className="text-faint">Planned: {plan}</span>
      </div>
    </Panel>
  );
}

/** Candlestick + overlay placeholders for the market monitor (§3.2 C). */
export function MarketChartPlaceholder({
  symbol,
  timeframe,
  barCount,
  overlays,
}: {
  symbol: string;
  timeframe: string;
  barCount: number;
  overlays: readonly string[];
}): JSX.Element {
  return (
    <div className="chart-placeholder">
      <strong>
        Candlestick chart for {symbol} {timeframe} — not rendered
      </strong>
      <span>
        {barCount} completed bars are available from the API contract, but this build deliberately does not draw candles: OHLC
        rendering, crosshair and time-axis scaling are exactly what <span className="text-mono">lightweight-charts</span> is for, and
        a recharts candlestick would be a bespoke shape that looks finished without being trustworthy.
      </span>
      <span className="text-faint">
        Overlays that must be plotted when this is implemented: {overlays.join(', ')}. Bars marked incomplete must never be used for
        signals (§5.2).
      </span>
    </div>
  );
}

export const CHART_KINDS: Record<AnalyticsChartId, 'bar' | 'line' | 'area' | 'histogram'> = {
  cumulative_realized_pnl: 'line',
  equity_curve: 'line',
  win_loss_distribution: 'bar',
  pnl_by_hour: 'bar',
  pnl_by_weekday: 'bar',
  pnl_by_instrument: 'bar',
  pnl_by_strategy: 'bar',
  pnl_by_regime: 'bar',
  slippage_distribution: 'histogram',
  holding_time_distribution: 'histogram',
  consecutive_streaks: 'line',
  session_trade_count_winrate: 'bar',
};
